import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Minus, Mail, Package, Plus, Share2, ShoppingCart } from "lucide-react";
import Reveal from "@/components/Reveal";
import { api, formatApiErrorDetail, imgUrl, usd } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n";
import { GROUP_ICONS } from "@/kitConfigs";
import { toast } from "sonner";

export default function KitBuilderBase({ config }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { add, setQty, setOpen } = useCart();
  const [products, setProducts] = useState([]);
  const [choices, setChoices] = useState({});
  const [qtys, setQtys] = useState({});
  const [searchParams] = useSearchParams();
  const [shareEmail, setShareEmail] = useState("");
  const [emailing, setEmailing] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  // Apply a shared kit link (?sel=Name:qty,Name:qty) once products arrive.
  // Names are used so links keep working across environments.
  useEffect(() => {
    if (!products.length) return;
    const raw = searchParams.get("sel");
    if (!raw) return;
    const nextChoices = {};
    const nextQtys = {};
    for (const part of raw.split(",")) {
      const sep = part.lastIndexOf(":");
      if (sep < 0) continue;
      const name = decodeURIComponent(part.slice(0, sep));
      const qty = Math.max(1, Math.min(99, parseInt(part.slice(sep + 1), 10) || 1));
      const group = config.groups.find((g) => g.names.includes(name));
      const product = products.find((p) => p.name === name);
      if (!group || !product) continue;
      if (group.type === "choice") nextChoices[group.key] = product.id;
      else nextQtys[product.id] = qty;
    }
    setChoices(nextChoices);
    setQtys(nextQtys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products]);

  const groups = config.groups.map((g) => ({
    ...g,
    items: products.filter((p) => g.names.includes(p.name)),
  }));

  const selection = useMemo(() => {
    const sel = [];
    for (const g of config.groups) {
      const items = products.filter((p) => g.names.includes(p.name));
      if (g.type === "choice") {
        const p = items.find((x) => x.id === choices[g.key]);
        if (p) sel.push({ groupKey: g.key, product: p, qty: 1 });
      } else {
        for (const p of items) {
          const q = qtys[p.id] || 0;
          if (q > 0) sel.push({ groupKey: g.key, product: p, qty: q });
        }
      }
    }
    return sel;
  }, [config, products, choices, qtys]);

  const { subtotal, pct, code, total, itemCount } = useMemo(() => {
    const sub = Math.round(selection.reduce((a, s) => a + s.product.price * s.qty, 0) * 100) / 100;
    const { pct: p, code: c } = config.tier(selection);
    const discount = Math.round(sub * p) / 100;
    return {
      subtotal: sub, pct: p, code: c,
      total: Math.round((sub - discount) * 100) / 100,
      itemCount: selection.reduce((a, s) => a + s.qty, 0),
    };
  }, [selection, config]);

  const requiredMet = config.groups
    .filter((g) => g.required)
    .every((g) => choices[g.key]);

  const step = (id, delta) =>
    setQtys((prev) => ({ ...prev, [id]: Math.max(0, Math.min(99, (prev[id] || 0) + delta)) }));

  const addKit = () => {
    if (!selection.length || !requiredMet) return;
    for (const s of selection) {
      add(s.product);
      if (s.qty > 1) setQty(s.product.id, s.qty);
    }
    setOpen(false);
    if (code) localStorage.setItem("kinos_kit_promo", code);
    navigate("/checkout");
  };

  const shareKit = async () => {
    if (!selection.length) return;
    try {
      await navigator.clipboard.writeText(shareUrl());
      toast.success(t("kit_share_copied"));
    } catch {
      window.prompt(t("kit_share"), shareUrl());
    }
  };

  const shareUrl = () => {
    const parts = selection.map((s) => `${encodeURIComponent(s.product.name)}:${s.qty}`);
    return `${window.location.origin}${config.basePath}?sel=${parts.join(",")}`;
  };

  const emailKit = async () => {
    if (!selection.length) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(shareEmail.trim())) {
      toast.error(t("kit_email_invalid"));
      return;
    }
    setEmailing(true);
    try {
      await api.post("/kit/share-email", {
        email: shareEmail.trim(),
        url: shareUrl(),
        kit: config.basePath,
      });
      toast.success(t("kit_email_sent"));
      setShareEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEmailing(false);
    }
  };

  return (
    <main data-testid={config.pageTestId} className="min-h-screen bg-[#0B1120] px-6 md:px-12 lg:px-24 pt-32 pb-24">
      <Reveal y={24}>
        <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">{t(config.labelKey)}</p>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-6xl tracking-tight mb-4">
          {t(config.titleKey)}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 max-w-xl mb-12">{t(config.subKey)}</p>
      </Reveal>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-8 space-y-10">
          {groups.map((g, gi) => {
            const Icon = GROUP_ICONS[g.key] || Package;
            return (
              <Reveal key={g.key} delay={0.05 + gi * 0.05}>
                <div>
                  <h2 className="font-display font-semibold text-white text-xl mb-4 flex items-center gap-3">
                    <Icon size={20} className="text-[#ED1C24]" /> {t(g.titleKey)}
                  </h2>
                  {g.type === "choice" ? (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {g.items.map((p) => (
                        <button
                          key={p.id}
                          data-testid={`${g.key}-choice-${p.id}`}
                          onClick={() => setChoices((prev) => ({ ...prev, [g.key]: prev[g.key] === p.id ? null : p.id }))}
                          className={`text-left border p-5 transition-colors duration-300 ${
                            choices[g.key] === p.id
                              ? "border-[#ED1C24] bg-[#ED1C24]/10"
                              : "border-white/10 bg-[#0F172A]/60 hover:border-white/30"
                          }`}
                        >
                          <img src={imgUrl(p.image)} alt={p.name} className="h-28 w-full object-contain mb-3" />
                          <p className="font-display font-semibold text-white text-base leading-snug">{p.name}</p>
                          <p className="text-[#FFC61A] font-semibold mt-1">{usd(p.price)}</p>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="border border-white/10 divide-y divide-white/10">
                      {g.items.map((p) => {
                        const q = qtys[p.id] || 0;
                        return (
                          <div key={p.id} data-testid={`${g.key}-item-${p.id}`} className="flex items-center gap-4 px-5 py-4 bg-[#0F172A]/60">
                            <img src={imgUrl(p.image)} alt={p.name} className="w-14 h-14 object-contain shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-display font-semibold text-white text-sm sm:text-base truncate">{p.name}</p>
                              <p className="text-[#FFC61A] text-sm">{usd(p.price)}</p>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <button
                                data-testid={`${g.key}-minus-${p.id}`}
                                onClick={() => step(p.id, -1)}
                                disabled={q === 0}
                                className="w-8 h-8 flex items-center justify-center border border-white/15 text-slate-300 hover:border-[#ED1C24] hover:text-white disabled:opacity-30 transition-colors"
                                aria-label="Decrease"
                              >
                                <Minus size={14} />
                              </button>
                              <span data-testid={`${g.key}-qty-${p.id}`} className="w-6 text-center text-white font-medium">{q}</span>
                              <button
                                data-testid={`${g.key}-plus-${p.id}`}
                                onClick={() => step(p.id, 1)}
                                className="w-8 h-8 flex items-center justify-center border border-white/15 text-slate-300 hover:border-[#ED1C24] hover:text-white transition-colors"
                                aria-label="Increase"
                              >
                                <Plus size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-32 border border-white/10 bg-[#0F172A] p-6">
            <h3 className="font-display font-semibold text-white text-lg mb-5 flex items-center gap-2">
              <Package size={18} className="text-[#ED1C24]" /> {t("kit_summary")}
            </h3>
            <div className="space-y-2 text-sm mb-5">
              <div className="flex justify-between text-slate-400">
                <span>{t("kit_items")}</span>
                <span data-testid="kit-item-count" className="text-white">{itemCount}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>{t("kit_subtotal")}</span>
                <span data-testid="kit-subtotal" className="text-white">{usd(subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>{t("kit_discount")}</span>
                <span data-testid="kit-discount" className={pct ? "text-emerald-400" : "text-slate-500"}>
                  {pct ? `-${pct}% (${code})` : "—"}
                </span>
              </div>
              <div className="border-t border-white/10 pt-3 flex justify-between">
                <span className="font-display font-semibold text-white">{t("kit_total")}</span>
                <span data-testid="kit-total" className="font-display font-semibold text-[#FFC61A] text-lg">{usd(total)}</span>
              </div>
            </div>
            {!pct && requiredMet && <p className="text-xs text-slate-500 mb-4">{t(config.hintKey)}</p>}
            {pct > 0 && <p className="text-xs text-emerald-400 mb-4">{t("kit_discount_applied")}</p>}
            <button
              data-testid="kit-add-to-cart-btn"
              onClick={addKit}
              disabled={itemCount === 0 || !requiredMet}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-40 text-white font-medium py-3 transition-colors duration-300"
            >
              <ShoppingCart size={16} /> {t("kit_add")}
            </button>
            <button
              data-testid="kit-share-btn"
              onClick={shareKit}
              disabled={itemCount === 0}
              className="w-full mt-3 inline-flex items-center justify-center gap-2 border border-white/15 hover:border-[#FFC61A] disabled:opacity-40 text-slate-300 hover:text-[#FFC61A] text-sm py-2.5 transition-colors duration-300"
            >
              <Share2 size={14} /> {t("kit_share")}
            </button>
            <div className="flex gap-2 mt-3">
              <input
                data-testid="kit-email-input"
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder={t("kit_email_ph")}
                className="flex-1 min-w-0 bg-[#0B1120] border border-white/10 text-slate-100 placeholder:text-slate-600 text-sm px-3 py-2.5 outline-none focus:border-[#ED1C24] transition-colors"
              />
              <button
                data-testid="kit-email-btn"
                onClick={emailKit}
                disabled={itemCount === 0 || emailing}
                className="inline-flex items-center gap-2 border border-white/15 hover:border-[#FFC61A] disabled:opacity-40 text-slate-300 hover:text-[#FFC61A] text-sm px-4 transition-colors duration-300"
              >
                <Mail size={14} /> {emailing ? "…" : t("kit_email_btn")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
